import { ReactElement, useCallback, useMemo, useEffect } from 'react';
import {
  Typography,
  Stack,
  DialogContentText,
  TextField,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFormik, FormikHelpers } from 'formik';

import { createTemplateByRoute } from '~services/templateRoute';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { hasError, getHelperText } from '~utils/formHelpers';

import { DialogCreateTemplateByRouteProps } from './types';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';
import {
  DialogCreateTemplateByRouteContainer,
  DialogCreateTemplateByRouteTitle,
  DialogCreateTemplateByRouteTitleCloseIcon,
  DialogCreateTemplateByRouteContent,
} from './styles';

const DialogCreateTemplateByRoute = ({
  open,
  onClose,
  onConfirmSuccess,
  routeId,
}: DialogCreateTemplateByRouteProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingCreateTemplateByRoute, , executeCreateTemplateByRoute] =
    useLazyRequest({
      request: createTemplateByRoute,
      withPostSuccess: (response) => {
        const routeCode = response.data?.data.routeCode.toUpperCase();
        const message = `La plantilla #${routeCode} fue creada correctamente`;

        enqueueSnackbar(message, { variant: 'success' });

        onConfirmSuccess?.();
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo crear la plantilla, intente nuevamente', {
          variant: 'error',
        });
      },
    });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      await executeCreateTemplateByRoute({
        routeId,
        routeName: values[FIELDS_NAME.NAME],
      });

      setSubmitting(false);
    },
    [executeCreateTemplateByRoute, routeId],
  );

  const {
    resetForm,
    submitForm,
    isSubmitting,
    getFieldProps,
    touched,
    errors,
    dirty,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogCreateTemplateByRouteContainer open={open} maxWidth="md">
      <DialogCreateTemplateByRouteTitle>
        <Typography component="h2" variant="h6">
          Guardar plantilla a través de ruta
        </Typography>

        <DialogCreateTemplateByRouteTitleCloseIcon
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </DialogCreateTemplateByRouteTitleCloseIcon>
      </DialogCreateTemplateByRouteTitle>

      <DialogCreateTemplateByRouteContent dividers>
        <Stack spacing={2}>
          <DialogContentText color="text.secondary" variant="caption">
            Al confirmar esta acción, Se creará una plantilla a partir de la
            ruta seleccionada.
          </DialogContentText>

          <TextField
            label="Nombre de plantilla"
            autoComplete="off"
            fullWidth
            error={hasError(touched, errors, FIELDS_NAME.NAME)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.NAME)}
            {...getFieldProps(FIELDS_NAME.NAME)}
          />
        </Stack>
      </DialogCreateTemplateByRouteContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingCreateTemplateByRoute || isSubmitting}
          disabled={disabledSubmit}
        >
          Guardar
        </LoadingButton>
      </DialogActions>
    </DialogCreateTemplateByRouteContainer>
  );
};

export default DialogCreateTemplateByRoute;
